/**
 * DIPS2.0 登録機体一覧ページ
 * デザイン: DESIGN.md / DESIGN_PRINCIPLES.md 参照
 */

import React, { useState, useEffect, useCallback } from 'react';
import { dipsApi } from '../services/api';
import { Icon } from '../components/icons';
import { Button, Card, Badge, Alert, Modal, InfoCell, StatusBadge } from '../components/ui';

import { C } from '../ColorTokens';

// 機体種類のラベル
const AIRCRAFT_TYPE_LABELS = {
  '1': '回転翼（マルチローター）',
  '2': '回転翼（ヘリコプター）',
  '3': '固定翼',
  '4': '固定翼（VTOL）',
};

// 所有者区分のラベル
const OWNER_CLASSIFICATION_LABELS = {
  '1': '個人',
  '9': '法人',
};

// ヘルパー関数: ネストされたデータを取得
const getAircraftInfo = (aircraft) => aircraft?.aircraft_information || aircraft;
const getOwnerInfo = (aircraft) => aircraft?.owner_information;
const getUserInfo = (aircraft) => aircraft?.user_information;

// 日付フォーマット
const formatDate = (dateStr) => {
  if (!dateStr) return '—';
  try {
    return new Date(dateStr).toLocaleDateString('ja-JP');
  } catch {
    return dateStr;
  }
};

export default function DipsAircrafts() {
  const [dipsAircrafts, setDipsAircrafts] = useState(null);
  const [dipsLoading, setDipsLoading] = useState(false);
  const [dipsError, setDipsError] = useState(null);
  const [dipsAuthenticated, setDipsAuthenticated] = useState(dipsApi.isAuthenticated());
  const [selectedAircraft, setSelectedAircraft] = useState(null);

  // 飛行計画
  const [flightPlans, setFlightPlans] = useState([]);
  const [flightPlansLoading, setFlightPlansLoading] = useState(false);
  const [flightPlansError, setFlightPlansError] = useState(null);
  const [selectedFlightPlan, setSelectedFlightPlan] = useState(null);
  const [flightPlanAircraft, setFlightPlanAircraft] = useState(null); // 飛行計画を表示中の機体
  const [flightPlanAuthenticated, setFlightPlanAuthenticated] = useState(dipsApi.isFlightPlanAuthenticated());
  const [flightPlanAuthLoading, setFlightPlanAuthLoading] = useState(false);
  const [searchDate, setSearchDate] = useState(() => {
    // デフォルトは今日
    const today = new Date();
    return today.toISOString().split('T')[0]; // "YYYY-MM-DD"
  });
  const [scanMode, setScanMode] = useState(false); // 範囲スキャンモード
  const [drawerTab, setDrawerTab] = useState('basic'); // 機体詳細ドロワータブ
  const [scanProgress, setScanProgress] = useState('');
  const [scanResults, setScanResults] = useState([]); // スキャン結果

  // 飛行計画登録フォーム
  const [showReportForm, setShowReportForm] = useState(false);
  const [reportLoading, setReportLoading] = useState(false);
  const [reportResult, setReportResult] = useState(null);
  const [reportForm, setReportForm] = useState({
    // 飛行日時
    flightDate: new Date().toISOString().split('T')[0],
    startTime: '09:00',
    endTime: '17:00',
    // 飛行エリア（緯度・経度）
    latitude: '35.6895',  // 東京
    longitude: '139.6917',
    radius: '500', // メートル
    // 飛行目的
    purpose: '空撮',
    // 高度
    maxAltitude: '150',
    // 備考
    remarks: '',
  });

  // DIPS機体情報取得
  const fetchDipsAircrafts = useCallback(async () => {
    if (!dipsApi.isAuthenticated()) {
      setDipsAuthenticated(false);
      return;
    }
    setDipsLoading(true);
    setDipsError(null);
    try {
      const data = await dipsApi.getAircrafts();
      setDipsAircrafts(data);
      setDipsAuthenticated(true);
    } catch (err) {
      setDipsError(err.message);
      if (err.message.includes('認証')) {
        setDipsAuthenticated(false);
      }
    } finally {
      setDipsLoading(false);
    }
  }, []);

  // 日付を "yyyyMMdd HHmm" 形式にフォーマット
  const formatDipsDateTime = (date) => {
    const y = date.getFullYear();
    const m = String(date.getMonth() + 1).padStart(2, '0');
    const d = String(date.getDate()).padStart(2, '0');
    const h = String(date.getHours()).padStart(2, '0');
    const min = String(date.getMinutes()).padStart(2, '0');
    return `${y}${m}${d} ${h}${min}`;
  };

  // DIPS飛行計画取得（地理的範囲と日時で検索）
  const fetchFlightPlans = useCallback(async () => {
    console.log('[DIPS] fetchFlightPlans called, searchDate:', searchDate);
    console.log('[DIPS] isFlightPlanAuthenticated:', dipsApi.isFlightPlanAuthenticated());

    if (!dipsApi.isFlightPlanAuthenticated()) {
      console.log('[DIPS] Flight plan not authenticated, skipping');
      return;
    }

    setFlightPlansLoading(true);
    setFlightPlansError(null);
    try {
      // 選択した日付の00:00から23:59まで（24時間以内）
      const [year, month, day] = searchDate.split('-').map(Number);
      const startTime = new Date(year, month - 1, day, 0, 0);
      const finishTime = new Date(year, month - 1, day, 23, 59);

      // FPR APIの正しいリクエスト構造
      // features: 検索範囲（日本全体をカバーする広い円）
      // 東京を中心に半径500km
      const searchParams = {
        features: {
          type: 'Circle',
          center: [139.6917, 35.6895], // 東京
          radius: 500000, // 500km
        },
        startTime: formatDipsDateTime(startTime),
        finishTime: formatDipsDateTime(finishTime),
        allFlightPlan: '1', // 自分の飛行計画のみ
      };

      console.log('[DIPS] Flight plan search params:', searchParams);
      const data = await dipsApi.getFlightPlans(searchParams);
      console.log('[DIPS] Flight plans response:', data);

      // レスポンスから飛行計画リストを抽出
      const plans = Array.isArray(data) ? data : data.flight_plans || data.flightPlans || data.items || data.results || [];
      setFlightPlans(plans);
    } catch (err) {
      console.error('[DIPS] Flight plans error:', err);
      setFlightPlansError(err.message);
    } finally {
      setFlightPlansLoading(false);
    }
  }, [searchDate]);

  // DIPS認証開始
  const handleDipsAuth = async () => {
    console.log('[DIPS] handleDipsAuth called');
    setDipsLoading(true);
    setDipsError(null);
    try {
      console.log('[DIPS] Getting auth URL...');
      const result = await dipsApi.getAuthUrl('aircraft');
      console.log('[DIPS] Auth URL result:', result);
      const { authUrl } = result;

      if (!authUrl) {
        throw new Error('認証URLが取得できませんでした');
      }

      // postMessageイベントリスナーを設定
      const handleMessage = (event) => {
        console.log('[DIPS] Received postMessage:', event.data);
        if (event.data?.type === 'DIPS_AUTH_SUCCESS') {
          console.log('[DIPS] Received auth success via postMessage');
          dipsApi.storeToken({
            accessToken: event.data.accessToken,
            refreshToken: event.data.refreshToken,
            expiresIn: event.data.expiresIn,
          });
          setDipsAuthenticated(true);
          setDipsLoading(false);
          fetchDipsAircrafts();
          window.removeEventListener('message', handleMessage);
        } else if (event.data?.type === 'DIPS_AUTH_ERROR') {
          console.error('[DIPS] Received auth error via postMessage:', event.data.error);
          setDipsError('認証エラー: ' + event.data.error);
          setDipsLoading(false);
          window.removeEventListener('message', handleMessage);
        }
      };
      window.addEventListener('message', handleMessage);
      console.log('[DIPS] postMessage listener added');

      console.log('[DIPS] Opening auth window:', authUrl);
      const authWindow = window.open(authUrl, 'dips_auth', 'width=600,height=700');

      if (!authWindow) {
        window.removeEventListener('message', handleMessage);
        throw new Error('ポップアップがブロックされました。ポップアップを許可してください。');
      }

      const checkAuth = setInterval(() => {
        try {
          if (authWindow?.closed) {
            clearInterval(checkAuth);
            console.log('[DIPS] Auth window closed, checking token...');
            setTimeout(() => {
              window.removeEventListener('message', handleMessage);
              if (dipsApi.isAuthenticated()) {
                console.log('[DIPS] Token found after window close');
                setDipsAuthenticated(true);
                setDipsLoading(false);
                fetchDipsAircrafts();
              } else {
                console.log('[DIPS] No token found after window close');
                setDipsLoading(false);
              }
            }, 1000);
          }
        } catch {
          // Cross-origin エラーは無視
        }
      }, 500);
    } catch (err) {
      console.error('[DIPS] Auth error:', err);
      setDipsError('認証エラー: ' + err.message);
      setDipsLoading(false);
    }
  };

  // DIPS認証済みなら機体情報を自動取得
  useEffect(() => {
    if (dipsAuthenticated && !dipsAircrafts) {
      fetchDipsAircrafts();
    }
  }, [dipsAuthenticated, dipsAircrafts, fetchDipsAircrafts]);

  // 認証完了時または日付変更時に飛行計画を取得
  useEffect(() => {
    console.log('[DIPS] useEffect check - flightPlanAuthenticated:', flightPlanAuthenticated, 'searchDate:', searchDate);
    if (flightPlanAuthenticated && !scanMode) {
      console.log('[DIPS] Calling fetchFlightPlans from useEffect');
      fetchFlightPlans();
    }
  }, [flightPlanAuthenticated, searchDate, scanMode, fetchFlightPlans]);

  // 範囲スキャン: 過去2週間 + 未来2ヶ月を検索
  const scanDateRange = async () => {
    if (!dipsApi.isFlightPlanAuthenticated()) {
      setFlightPlansError('飛行計画認証が必要です');
      return;
    }

    setScanMode(true);
    setScanResults([]);
    setFlightPlans([]);
    setFlightPlansError(null);

    const today = new Date();
    const results = [];

    // 過去2週間（14日）+ 未来2ヶ月（60日）= 約74日
    const startOffset = -14; // 2週間前
    const endOffset = 60;    // 2ヶ月後

    for (let i = startOffset; i <= endOffset; i++) {
      const targetDate = new Date(today);
      targetDate.setDate(today.getDate() + i);

      const dateStr = targetDate.toISOString().split('T')[0];
      setScanProgress(`検索中: ${dateStr} (${i - startOffset + 1}/${endOffset - startOffset + 1})`);

      try {
        const startTime = new Date(targetDate);
        startTime.setHours(0, 0, 0, 0);
        const finishTime = new Date(targetDate);
        finishTime.setHours(23, 59, 0, 0);

        const searchParams = {
          features: {
            type: 'Circle',
            center: [139.6917, 35.6895],
            radius: 500000,
          },
          startTime: formatDipsDateTime(startTime),
          finishTime: formatDipsDateTime(finishTime),
          allFlightPlan: '1',
        };

        const data = await dipsApi.getFlightPlans(searchParams);
        const plans = Array.isArray(data) ? data : data.flight_plans || data.flightPlans || data.items || data.results || [];

        if (plans.length > 0) {
          results.push({
            date: dateStr,
            count: plans.length,
            plans: plans,
          });
          console.log(`[DIPS] Found ${plans.length} plans on ${dateStr}`);
        }
      } catch (err) {
        // エラーは無視して続行
        console.log(`[DIPS] Error on ${dateStr}:`, err.message);
      }

      // API負荷軽減のため少し待機
      await new Promise(resolve => setTimeout(resolve, 100));
    }

    setScanProgress('');
    setScanResults(results);
    setScanMode(false);

    console.log('[DIPS] Scan complete. Results:', results);
  };

  // 飛行計画表示用（機体選択は不要になったが互換性のため残す）
  const handleShowFlightPlans = (aircraft) => {
    console.log('[DIPS] handleShowFlightPlans called');
    setFlightPlanAircraft(aircraft);
  };

  // 飛行計画認証開始
  const handleFlightPlanAuth = async () => {
    console.log('[DIPS] handleFlightPlanAuth called');
    setFlightPlanAuthLoading(true);
    setFlightPlansError(null);
    try {
      const result = await dipsApi.getAuthUrl('flightPlan');
      const { authUrl } = result;

      if (!authUrl) {
        throw new Error('認証URLが取得できませんでした');
      }

      // postMessageイベントリスナーを設定
      const handleMessage = (event) => {
        console.log('[DIPS FlightPlan] Received postMessage:', event.data);
        if (event.data?.type === 'DIPS_AUTH_SUCCESS') {
          console.log('[DIPS FlightPlan] Received auth success via postMessage');
          dipsApi.storeFlightPlanToken({
            accessToken: event.data.accessToken,
            refreshToken: event.data.refreshToken,
            expiresIn: event.data.expiresIn,
          });
          setFlightPlanAuthenticated(true);
          setFlightPlanAuthLoading(false);
          window.removeEventListener('message', handleMessage);
        } else if (event.data?.type === 'DIPS_AUTH_ERROR') {
          console.error('[DIPS FlightPlan] Received auth error:', event.data.error);
          setFlightPlansError('認証エラー: ' + event.data.error);
          setFlightPlanAuthLoading(false);
          window.removeEventListener('message', handleMessage);
        }
      };
      window.addEventListener('message', handleMessage);

      const authWindow = window.open(authUrl, 'dips_flightplan_auth', 'width=600,height=700');

      if (!authWindow) {
        window.removeEventListener('message', handleMessage);
        throw new Error('ポップアップがブロックされました。');
      }

      const checkAuth = setInterval(() => {
        try {
          if (authWindow?.closed) {
            clearInterval(checkAuth);
            setTimeout(() => {
              window.removeEventListener('message', handleMessage);
              if (dipsApi.isFlightPlanAuthenticated()) {
                setFlightPlanAuthenticated(true);
                setFlightPlanAuthLoading(false);
              } else {
                setFlightPlanAuthLoading(false);
              }
            }, 1000);
          }
        } catch {
          // Cross-origin エラーは無視
        }
      }, 500);
    } catch (err) {
      console.error('[DIPS FlightPlan] Auth error:', err);
      setFlightPlansError('認証エラー: ' + err.message);
      setFlightPlanAuthLoading(false);
    }
  };

  // 飛行計画通報（登録）
  const handleReportFlightPlan = async () => {
    if (!dipsApi.isFlightPlanAuthenticated()) {
      setFlightPlansError('飛行計画認証が必要です');
      return;
    }

    setReportLoading(true);
    setReportResult(null);
    setFlightPlansError(null);

    try {
      // 日時をDIPS形式に変換
      const [year, month, day] = reportForm.flightDate.split('-').map(Number);
      const [startH, startM] = reportForm.startTime.split(':').map(Number);
      const [endH, endM] = reportForm.endTime.split(':').map(Number);

      const startDateTime = new Date(year, month - 1, day, startH, startM);
      const endDateTime = new Date(year, month - 1, day, endH, endM);

      // FPR API リクエストボディ
      const reportData = {
        // 飛行エリア（円形）
        features: {
          type: 'Circle',
          center: [parseFloat(reportForm.longitude), parseFloat(reportForm.latitude)],
          radius: parseInt(reportForm.radius, 10),
        },
        // 飛行開始・終了時刻
        startTime: formatDipsDateTime(startDateTime),
        finishTime: formatDipsDateTime(endDateTime),
        // 飛行目的
        flightPurpose: reportForm.purpose,
        // 最大高度
        maxAltitude: parseInt(reportForm.maxAltitude, 10),
        // 備考
        remarks: reportForm.remarks || undefined,
      };

      console.log('[DIPS] Report flight plan:', reportData);
      const result = await dipsApi.reportFlightPlan(reportData);
      console.log('[DIPS] Report result:', result);

      setReportResult({ success: true, data: result });
      // 成功したら検索を再実行
      fetchFlightPlans();
    } catch (err) {
      console.error('[DIPS] Report error:', err);
      setReportResult({ success: false, error: err.message });
    } finally {
      setReportLoading(false);
    }
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 24, fontFamily: "'Noto Sans JP', sans-serif" }}>

      {/* ════════════════════════════════════
          ページヘッダー
      ════════════════════════════════════ */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 12 }}>
        {/* タイトル */}
        <div>
          <h1 style={{
            display: 'flex', alignItems: 'center', gap: 10,
            fontFamily: "'Space Grotesk', sans-serif",
            fontSize: 28, fontWeight: 700, letterSpacing: '-0.5px',
            color: C.primary, margin: 0, lineHeight: 1.2,
          }}>
            <span
              className="material-symbols-outlined"
              style={{
                fontSize: 28,
                color: C.primary,
                fontVariationSettings: "'FILL' 1, 'wght' 500, 'GRAD' 0, 'opsz' 24",
              }}
            >
              airplanemode_active
            </span>
            登録機体一覧
          </h1>
          <p style={{ fontSize: 13, color: C.onSurfaceVariant, marginTop: 4 }}>
            国土交通省ドローン情報基盤システム（DIPS2.0）連携
          </p>
        </div>

        {/* アクションボタン群 */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
          {dipsAuthenticated ? (
            <>
              {/* 連携済みバッジ */}
              <StatusBadge icon="check_circle" variant="secondary">連携済み</StatusBadge>
              {/* 同期ボタン */}
              <HeaderActionBtn icon="sync" label="同期" onClick={fetchDipsAircrafts} disabled={dipsLoading} />
              {/* ログアウトボタン */}
              <HeaderActionBtn
                icon="logout"
                label="ログアウト"
                onClick={() => {
                  dipsApi.clearToken();
                  setDipsAuthenticated(false);
                  setDipsAircrafts(null);
                  const logoutWindow = window.open(
                    'https://www.dips-regdev.mlit.go.jp/auth/realms/drs-utm/protocol/openid-connect/logout',
                    'dips_logout',
                    'width=500,height=400'
                  );
                  if (logoutWindow) setTimeout(() => logoutWindow.close(), 500);
                }}
              />
            </>
          ) : (
            <StatusBadge icon="link_off" variant="error">DIPS2.0未連携</StatusBadge>
          )}
        </div>
      </div>

      {/* ════════════════════════════════════
          エラー表示
      ════════════════════════════════════ */}
      {dipsError && <Alert variant="error">{dipsError}</Alert>}

      {/* ════════════════════════════════════
          未認証状態（ローディング中は非表示）
      ════════════════════════════════════ */}
      {!dipsAuthenticated && !dipsError && !dipsLoading && (
        <UnauthenticatedCard onAuth={handleDipsAuth} loading={dipsLoading} />
      )}

      {/* ════════════════════════════════════
          ローディング
      ════════════════════════════════════ */}
      {dipsLoading && (
        <div style={{
          background: 'rgba(255,255,255,0.72)',
          backdropFilter: 'blur(20px)',
          WebkitBackdropFilter: 'blur(20px)',
          borderRadius: 24,
          border: `1px solid rgba(255,255,255,0.28)`,
          boxShadow: '0 8px 32px rgba(0,104,183,0.10)',
          padding: '48px 24px',
          display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 16,
        }}>
          <div style={{
            width: 48, height: 48,
            borderRadius: '50%',
            border: `3px solid ${C.surfaceContainer}`,
            borderTopColor: C.primaryContainer,
            animation: 'spin 0.8s linear infinite',
          }} />
          <div style={{ textAlign: 'center' }}>
            <p style={{ fontSize: 15, fontWeight: 500, color: C.onSurface, margin: 0 }}>機体情報を取得中...</p>
            <p style={{ fontSize: 13, color: C.onSurfaceVariant, marginTop: 4 }}>DIPS2.0 と通信しています</p>
          </div>
          <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
        </div>
      )}

      {/* ════════════════════════════════════
          所有者・使用者情報
      ════════════════════════════════════ */}
      {dipsAuthenticated && !dipsLoading && dipsAircrafts?.length > 0 && (() => {
        const firstAircraft = dipsAircrafts[0];
        const owner = getOwnerInfo(firstAircraft);
        const user = getUserInfo(firstAircraft);
        const isUserDifferent = user && user.owner_user_same_confirmation === '2';

        return (
          <div style={{ display: 'grid', gap: 20, gridTemplateColumns: isUserDifferent ? 'repeat(auto-fit, minmax(300px, 1fr))' : '1fr' }}>
            {/* 所有者情報カード */}
            {owner && (
              <InfoCard
                icon="person"
                title="所有者情報"
                badge={OWNER_CLASSIFICATION_LABELS[owner.owner_classification] || owner.owner_classification}
                rows={[
                  { label: '氏名/法人名',     value: owner.owner_fullname || owner.owner_corporation_name },
                  { label: 'フリガナ',         value: owner.owner_furigana },
                  { label: '生年月日',         value: owner.owner_birthday || null },
                  { label: '住所',             value: owner.owner_address },
                  { label: '電話番号',         value: owner.owner_tel },
                  { label: 'メールアドレス',   value: owner.owner_email_address },
                ]}
              />
            )}
            {/* 使用者情報カード（所有者と異なる場合のみ） */}
            {isUserDifferent && (
              <InfoCard
                icon="group"
                title="使用者情報"
                badge={OWNER_CLASSIFICATION_LABELS[user.user_classification] || user.user_classification}
                rows={[
                  { label: '氏名/法人名',     value: user.user_fullname || user.user_corporation_name },
                  { label: 'フリガナ',         value: user.user_furigana },
                  { label: '住所',             value: user.user_address },
                  { label: '電話番号',         value: user.user_tel },
                  { label: 'メールアドレス',   value: user.user_email_address },
                  ...(user.user_corporation_representative_name ? [{ label: '代表者名', value: user.user_corporation_representative_name }] : []),
                  ...(user.user_department_name ? [{ label: '部署名', value: user.user_department_name }] : []),
                ]}
              />
            )}
          </div>
        );
      })()}

      {/* ════════════════════════════════════
          機体一覧（連携後）
      ════════════════════════════════════ */}
      {dipsAuthenticated && !dipsLoading && (() => {
        const sortedAircrafts = dipsAircrafts ? [...dipsAircrafts].sort((a, b) => {
          const statusA = getAircraftInfo(a)?.aircraft_status;
          const statusB = getAircraftInfo(b)?.aircraft_status;
          if (statusA === '1' && statusB !== '1') return -1;
          if (statusA !== '1' && statusB === '1') return 1;
          return 0;
        }) : [];
        const activeCount = sortedAircrafts.filter(a => getAircraftInfo(a)?.aircraft_status === '1').length;
        const inactiveCount = sortedAircrafts.length - activeCount;

        return (
          <div>
            {/* セクションヘッダー */}
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 }}>
              <h2 style={{
                fontFamily: "'Space Grotesk', sans-serif",
                fontSize: 18, fontWeight: 700, color: C.onSurface, margin: 0,
                display: 'flex', alignItems: 'center', gap: 8,
              }}>
                <span className="material-symbols-outlined" style={{ fontSize: 20, color: C.primary, fontVariationSettings: "'FILL' 1" }}>
                  airplanemode_active
                </span>
                登録機体
              </h2>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <span style={{ fontSize: 12, color: C.outline, fontFamily: "'Space Grotesk', sans-serif" }}>
                  {sortedAircrafts.length} 機
                </span>
                {activeCount > 0 && (
                  <StatusBadge variant="secondary">有効 {activeCount}</StatusBadge>
                )}
                {inactiveCount > 0 && (
                  <StatusBadge variant="neutral">無効 {inactiveCount}</StatusBadge>
                )}
              </div>
            </div>

            {sortedAircrafts.length > 0 ? (
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(clamp(200px, calc(20% - 16px), 100%), 1fr))', gap: 20 }}>
                {sortedAircrafts.map((aircraft, idx) => (
                  <AircraftCard
                    key={idx}
                    aircraft={aircraft}
                    onSelect={() => { setSelectedAircraft(aircraft); setDrawerTab('basic'); }}
                    onFlightPlan={(e) => { e.stopPropagation(); handleShowFlightPlans(aircraft); }}
                  />
                ))}
              </div>
            ) : (
              <div style={{
                background: 'rgba(255,255,255,0.72)', backdropFilter: 'blur(20px)',
                borderRadius: 24, border: '1px solid rgba(255,255,255,0.28)',
                padding: '48px 24px', textAlign: 'center',
              }}>
                <span className="material-symbols-outlined" style={{ fontSize: 48, color: C.outlineVariant, display: 'block', marginBottom: 12 }}>
                  airplanemode_inactive
                </span>
                <p style={{ color: C.onSurfaceVariant, fontSize: 15 }}>DIPS2.0に登録されている機体はありません</p>
              </div>
            )}
          </div>
        );
      })()}

      {/* ════════════════════════════════════
          飛行計画セクション
      ════════════════════════════════════ */}
      {dipsAuthenticated && !dipsLoading && (
        <div style={{
          background: 'rgba(255,255,255,0.70)',
          backdropFilter: 'blur(16px)',
          WebkitBackdropFilter: 'blur(16px)',
          borderRadius: 24,
          border: '1px solid rgba(255,255,255,0.28)',
          boxShadow: '0 8px 32px rgba(0,104,183,0.08)',
          overflow: 'hidden',
        }}>
          {/* セクションヘッダー */}
          <div style={{
            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
            flexWrap: 'wrap', gap: 10,
            padding: '20px 24px 16px',
            borderBottom: `1px solid rgba(193,199,211,0.35)`,
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <span className="material-symbols-outlined" style={{ fontSize: 20, color: C.primary, fontVariationSettings: "'FILL' 1" }}>
                calendar_month
              </span>
              <span style={{ fontFamily: "'Space Grotesk', sans-serif", fontSize: 16, fontWeight: 700, color: C.onSurface }}>
                飛行計画
              </span>
              <StatusBadge variant={flightPlanAuthenticated ? 'secondary' : 'neutral'}>
                {flightPlanAuthenticated ? '認証済み' : '要認証'}
              </StatusBadge>
            </div>

            {/* アクション群 */}
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
              {flightPlanAuthenticated ? (
                <>
                  <input
                    type="date"
                    value={searchDate}
                    onChange={(e) => setSearchDate(e.target.value)}
                    disabled={scanMode}
                    style={{
                      padding: '6px 12px', borderRadius: 9999,
                      border: `1px solid ${C.outlineVariant}`,
                      background: 'rgba(255,255,255,0.80)',
                      fontSize: 13, color: C.onSurface,
                      fontFamily: "'Space Grotesk', sans-serif",
                      outline: 'none', cursor: scanMode ? 'not-allowed' : 'auto',
                    }}
                  />
                  <HeaderActionBtn icon="refresh" label="検索" onClick={() => { setScanResults([]); fetchFlightPlans(); }} disabled={flightPlansLoading || scanMode} />
                  <HeaderActionBtn icon="travel_explore" label={scanMode ? scanProgress : '範囲スキャン'} onClick={scanDateRange} disabled={flightPlansLoading || scanMode} />
                  <button
                    onClick={() => setShowReportForm(!showReportForm)}
                    style={{
                      display: 'inline-flex', alignItems: 'center', gap: 6,
                      padding: '7px 16px', borderRadius: 9999, border: 'none',
                      background: C.primaryContainer, color: C.onPrimary,
                      fontSize: 13, fontWeight: 600,
                      fontFamily: "'Space Grotesk', sans-serif", cursor: 'pointer',
                    }}
                  >
                    <span className="material-symbols-outlined" style={{ fontSize: 15 }}>add</span>
                    飛行計画通報
                  </button>
                </>
              ) : (
                <button
                  onClick={handleFlightPlanAuth}
                  disabled={flightPlanAuthLoading}
                  style={{
                    display: 'inline-flex', alignItems: 'center', gap: 6,
                    padding: '7px 28px', borderRadius: 9999, border: 'none',
                    background: C.primaryContainer, color: C.onPrimary,
                    fontSize: 13, fontWeight: 600,
                    fontFamily: "'Space Grotesk', sans-serif",
                    cursor: flightPlanAuthLoading ? 'not-allowed' : 'pointer',
                    opacity: flightPlanAuthLoading ? 0.6 : 1,
                  }}
                >
                  <span className="material-symbols-outlined" style={{ fontSize: 15, display: 'inline-block', transform: 'rotate(-45deg)' }}>link</span>
                  {flightPlanAuthLoading ? '認証中...' : '飛行計画認証'}
                </button>
              )}
            </div>
          </div>

          {/* コンテンツエリア */}
          <div style={{ padding: '20px 24px' }}>

            {/* 飛行計画通報フォーム */}
            {showReportForm && flightPlanAuthenticated && (
              <div style={{
                marginBottom: 20, padding: 20,
                background: `rgba(163,243,145,0.14)`,
                border: `1px solid rgba(33,109,28,0.18)`,
                borderRadius: 16,
              }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 16 }}>
                  <span className="material-symbols-outlined" style={{ fontSize: 18, color: C.secondary }}>add_circle</span>
                  <span style={{ fontSize: 14, fontWeight: 600, color: C.onSurface, fontFamily: "'Space Grotesk', sans-serif" }}>
                    飛行計画通報（新規登録）
                  </span>
                </div>

                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))', gap: 12 }}>
                  {[
                    { label: '飛行日', type: 'date', key: 'flightDate' },
                    { label: '開始時刻', type: 'time', key: 'startTime' },
                    { label: '終了時刻', type: 'time', key: 'endTime' },
                    { label: '緯度', type: 'text', key: 'latitude', placeholder: '例: 35.6895' },
                    { label: '経度', type: 'text', key: 'longitude', placeholder: '例: 139.6917' },
                    { label: '半径 (m)', type: 'number', key: 'radius', placeholder: '例: 500' },
                    { label: '最大高度 (m)', type: 'number', key: 'maxAltitude', placeholder: '例: 150' },
                    { label: '備考', type: 'text', key: 'remarks', placeholder: '任意' },
                  ].map(({ label, type, key, placeholder }) => (
                    <div key={key}>
                      <label style={{ display: 'block', fontSize: 10, fontWeight: 600, color: C.outline, textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: 4, fontFamily: "'Space Grotesk', sans-serif" }}>
                        {label}
                      </label>
                      <input
                        type={type}
                        value={reportForm[key]}
                        onChange={(e) => setReportForm({ ...reportForm, [key]: e.target.value })}
                        placeholder={placeholder}
                        style={{
                          width: '100%', padding: '8px 12px', borderRadius: 10,
                          border: `1px solid ${C.outlineVariant}`,
                          background: 'rgba(255,255,255,0.80)',
                          fontSize: 13, color: C.onSurface,
                          fontFamily: "'Noto Sans JP', sans-serif",
                          outline: 'none', boxSizing: 'border-box',
                        }}
                      />
                    </div>
                  ))}
                  <div>
                    <label style={{ display: 'block', fontSize: 10, fontWeight: 600, color: C.outline, textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: 4, fontFamily: "'Space Grotesk', sans-serif" }}>
                      飛行目的
                    </label>
                    <select
                      value={reportForm.purpose}
                      onChange={(e) => setReportForm({ ...reportForm, purpose: e.target.value })}
                      style={{
                        width: '100%', padding: '8px 12px', borderRadius: 10,
                        border: `1px solid ${C.outlineVariant}`,
                        background: 'rgba(255,255,255,0.80)',
                        fontSize: 13, color: C.onSurface,
                        fontFamily: "'Noto Sans JP', sans-serif",
                        outline: 'none', boxSizing: 'border-box',
                      }}
                    >
                      <option value="空撮">空撮</option>
                      <option value="測量">測量</option>
                      <option value="点検">点検</option>
                      <option value="農薬散布">農薬散布</option>
                      <option value="物資輸送">物資輸送</option>
                      <option value="趣味">趣味</option>
                      <option value="その他">その他</option>
                    </select>
                  </div>
                </div>

                {/* 送信ボタン */}
                <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginTop: 16 }}>
                  <button
                    onClick={handleReportFlightPlan}
                    disabled={reportLoading}
                    style={{
                      display: 'inline-flex', alignItems: 'center', gap: 8,
                      padding: '9px 20px', borderRadius: 9999, border: 'none',
                      background: C.primaryContainer, color: C.onPrimary,
                      fontSize: 14, fontWeight: 600,
                      fontFamily: "'Space Grotesk', sans-serif",
                      cursor: reportLoading ? 'not-allowed' : 'pointer',
                      opacity: reportLoading ? 0.6 : 1,
                    }}
                  >
                    <span className="material-symbols-outlined" style={{ fontSize: 16 }}>send</span>
                    {reportLoading ? '送信中...' : '飛行計画を通報'}
                  </button>
                  <HeaderActionBtn icon="close" label="キャンセル" onClick={() => setShowReportForm(false)} />
                </div>

                {/* 結果表示 */}
                {reportResult && (
                  <div style={{ marginTop: 14 }}>
                    <Alert variant={reportResult.success ? 'success' : 'error'}>
                      {reportResult.success ? '飛行計画を通報しました' : `エラー: ${reportResult.error}`}
                      {reportResult.success && (
                        <pre style={{ fontSize: 10, color: C.onSurfaceVariant, marginTop: 8, whiteSpace: 'pre-wrap', wordBreak: 'break-all' }}>
                          {JSON.stringify(reportResult.data, null, 2)}
                        </pre>
                      )}
                    </Alert>
                  </div>
                )}
              </div>
            )}

            {/* 未認証状態 */}
            {!flightPlanAuthenticated ? (
              <div style={{ textAlign: 'center', padding: '32px 0' }}>
                <span className="material-symbols-outlined" style={{ fontSize: 40, color: C.outlineVariant, display: 'block', marginBottom: 10 }}>lock</span>
                <p style={{ fontSize: 14, color: C.onSurfaceVariant }}>飛行計画の表示には別途認証が必要です</p>
                <p style={{ fontSize: 12, color: C.outline, marginTop: 4 }}>「飛行計画認証」ボタンを押してください</p>
              </div>

            ) : scanMode ? (
              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', padding: '28px 0', gap: 10 }}>
                <div style={{ width: 32, height: 32, borderRadius: '50%', border: `3px solid ${C.surfaceContainer}`, borderTopColor: C.primaryContainer, animation: 'spin 0.8s linear infinite' }} />
                <span style={{ fontSize: 14, fontWeight: 500, color: C.onSurface }}>{scanProgress}</span>
                <span style={{ fontSize: 12, color: C.onSurfaceVariant }}>過去2週間〜未来2ヶ月をスキャン中...</span>
              </div>

            ) : flightPlansLoading ? (
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '28px 0', gap: 12 }}>
                <div style={{ width: 28, height: 28, borderRadius: '50%', border: `3px solid ${C.surfaceContainer}`, borderTopColor: C.primaryContainer, animation: 'spin 0.8s linear infinite' }} />
                <span style={{ fontSize: 14, color: C.onSurfaceVariant }}>飛行計画を取得中...</span>
              </div>

            ) : flightPlansError ? (
              <div style={{ padding: '16px 0' }}>
                <Alert variant="error" title={`飛行計画の取得に失敗しました: ${flightPlansError}`}>
                  飛行計画APIには別途認証が必要な場合があります
                </Alert>
              </div>

            ) : scanResults.length > 0 ? (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, paddingBottom: 12, borderBottom: `1px solid rgba(193,199,211,0.35)` }}>
                  <span className="material-symbols-outlined" style={{ fontSize: 16, color: C.secondary }}>check_circle</span>
                  <span style={{ fontSize: 13, color: C.onSurfaceVariant }}>
                    スキャン完了: <strong style={{ color: C.onSurface }}>{scanResults.reduce((sum, r) => sum + r.count, 0)}件</strong> の飛行計画を {scanResults.length} 日で発見
                  </span>
                </div>
                {scanResults.map((result, idx) => (
                  <div key={idx} style={{ borderRadius: 14, border: `1px solid ${C.outlineVariant}`, overflow: 'hidden' }}>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '10px 16px', background: C.surfaceContainerLow }}>
                      <span style={{ fontSize: 13, fontWeight: 600, color: C.onSurface, fontFamily: "'Space Grotesk', sans-serif" }}>{result.date}</span>
                      <StatusBadge variant="primary">{result.count}件</StatusBadge>
                    </div>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 1, padding: '8px 12px', background: 'rgba(255,255,255,0.50)' }}>
                      {result.plans.map((plan, pIdx) => (
                        <div
                          key={pIdx}
                          onClick={() => setSelectedFlightPlan(plan)}
                          style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '8px 10px', borderRadius: 10, cursor: 'pointer', transition: 'background 0.15s' }}
                          onMouseEnter={e => e.currentTarget.style.background = C.surfaceContainerLow}
                          onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
                        >
                          <span style={{ fontSize: 13, color: C.onSurface }}>{plan.flight_purpose || plan.purpose || plan.flightPurpose || '飛行計画'}</span>
                          <span className="material-symbols-outlined" style={{ fontSize: 14, color: C.outline }}>chevron_right</span>
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>

            ) : flightPlans.length > 0 ? (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                {flightPlans.map((plan, idx) => (
                  <div
                    key={idx}
                    onClick={() => setSelectedFlightPlan(plan)}
                    style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', padding: '12px 16px', borderRadius: 14, border: `1px solid ${C.outlineVariant}`, background: 'rgba(255,255,255,0.60)', cursor: 'pointer', transition: 'background 0.15s, box-shadow 0.15s' }}
                    onMouseEnter={e => { e.currentTarget.style.background = C.surfaceContainerLow; e.currentTarget.style.boxShadow = '0 4px 16px rgba(0,104,183,0.10)'; }}
                    onMouseLeave={e => { e.currentTarget.style.background = 'rgba(255,255,255,0.60)'; e.currentTarget.style.boxShadow = 'none'; }}
                  >
                    <div>
                      <div style={{ fontSize: 14, fontWeight: 500, color: C.onSurface }}>{plan.flight_purpose || plan.purpose || '飛行計画'}</div>
                      <div style={{ fontSize: 12, color: C.onSurfaceVariant, marginTop: 3 }}>
                        {formatDate(plan.flight_start_datetime || plan.start_date)} ～ {formatDate(plan.flight_end_datetime || plan.end_date)}
                      </div>
                    </div>
                    <StatusBadge variant="primary" style={{ flexShrink: 0, marginLeft: 12 }}>
                      {plan.status || '通報済み'}
                    </StatusBadge>
                  </div>
                ))}
              </div>

            ) : (
              <div style={{ textAlign: 'center', padding: '32px 0' }}>
                <span className="material-symbols-outlined" style={{ fontSize: 40, color: C.outlineVariant, display: 'block', marginBottom: 10 }}>calendar_month</span>
                <p style={{ fontSize: 14, color: C.onSurfaceVariant }}>{searchDate} の飛行計画はありません</p>
                <p style={{ fontSize: 12, color: C.outline, marginTop: 4 }}>「範囲スキャン」で広範囲を検索できます</p>
              </div>
            )}
          </div>
        </div>
      )}

      {/* 飛行計画詳細モーダル */}
      <Modal
        isOpen={!!selectedFlightPlan}
        onClose={() => setSelectedFlightPlan(null)}
        title="飛行計画詳細"
        size="lg"
      >
        {selectedFlightPlan && (
          <div className="space-y-4">
            <div className="bg-slate-50 rounded-lg p-4 max-h-96 overflow-auto">
              <pre className="text-xs text-slate-600 whitespace-pre-wrap">
                {JSON.stringify(selectedFlightPlan, null, 2)}
              </pre>
            </div>
          </div>
        )}
      </Modal>

      {/* ════════════════════════════════════
          機体詳細ボトムシートドロワー
      ════════════════════════════════════ */}
      {selectedAircraft && (
        <AircraftDrawer
          aircraft={selectedAircraft}
          tab={drawerTab}
          onTabChange={setDrawerTab}
          onClose={() => setSelectedAircraft(null)}
        />
      )}
    </div>
  );
}

// 詳細行コンポーネント
function DetailRow({ label, value }) {
  return (
    <div className="flex items-start gap-2">
      <span className="text-slate-400 w-24 flex-shrink-0">{label}:</span>
      <span className="text-slate-900">{value || '—'}</span>
    </div>
  );
}

// ---- 所有者/使用者情報カード ----
function InfoCard({ icon, title, badge, rows }) {
  return (
    <div style={{
      background: 'rgba(255,255,255,0.70)',
      backdropFilter: 'blur(16px)',
      WebkitBackdropFilter: 'blur(16px)',
      borderRadius: 24,
      border: '1px solid rgba(255,255,255,0.28)',
      boxShadow: '0 8px 32px rgba(0,104,183,0.08)',
      overflow: 'hidden',
    }}>
      {/* カードヘッダー */}
      <div style={{
        display: 'flex', alignItems: 'center', gap: 10,
        padding: '16px 20px 14px',
        borderBottom: '1px solid rgba(193,199,211,0.35)',
      }}>
        <span className="material-symbols-outlined" style={{ fontSize: 18, color: C.primary, fontVariationSettings: "'FILL' 1" }}>
          {icon}
        </span>
        <span style={{ fontFamily: "'Space Grotesk', sans-serif", fontSize: 15, fontWeight: 700, color: C.onSurface }}>
          {title}
        </span>
        {badge && (
          <span style={{
            fontSize: 10, fontWeight: 600, padding: '2px 10px', borderRadius: 9999,
            background: C.surfaceContainerLow, color: C.onSurfaceVariant,
            fontFamily: "'Space Grotesk', sans-serif", marginLeft: 2,
          }}>
            {badge}
          </span>
        )}
      </div>
      {/* 行グリッド */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(clamp(200px, calc(33.333% - 7px), 100%), 1fr))', gap: 10, padding: 16 }}>
        {rows.map(({ label, value }) => (
          <div key={label} style={{
            background: C.surfaceContainerLow,
            borderRadius: 12, padding: '10px 14px',
            border: `1px solid rgba(193,199,211,0.25)`,
          }}>
            <div style={{ fontSize: 9, fontFamily: "'Space Grotesk', sans-serif", fontWeight: 600, color: C.outline, textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: 3 }}>
              {label}
            </div>
            <div style={{ fontSize: 13, fontWeight: 500, color: value ? C.onSurface : C.outlineVariant }}>
              {value || '—'}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

// ---- 機体カード ----
function AircraftCard({ aircraft, onSelect, onFlightPlan }) {
  const [hovered, setHovered] = useState(false);
  const info = getAircraftInfo(aircraft);
  const isActive = info?.aircraft_status === '1';

  const statusBg    = isActive ? C.secondaryContainer  : C.surfaceContainer;
  const statusColor = isActive ? C.secondary           : C.onSurfaceVariant;
  const statusLabel = isActive ? '有効' : info?.aircraft_status === '2' ? '期限切れ' : '抹消済';

  return (
    <div
      onClick={onSelect}
      style={{
        position: 'relative',
        background: 'rgba(255,255,255,0.70)',
        backdropFilter: 'blur(16px)',
        WebkitBackdropFilter: 'blur(16px)',
        borderRadius: 24,
        border: '1px solid rgba(255,255,255,0.28)',
        boxShadow: hovered
          ? '0 12px 40px rgba(0,104,183,0.16)'
          : '0 8px 32px rgba(0,104,183,0.08)',
        padding: '20px 20px 16px',
        cursor: 'pointer',
        transition: 'transform 0.25s, box-shadow 0.25s, opacity 0.2s',
        transform: hovered ? 'scale(1.02) translateY(-4px)' : 'scale(1) translateY(0)',
        opacity: isActive ? 1 : 0.75,
        display: 'flex',
        flexDirection: 'column',
        gap: 12,
      }}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
    >
      {/* ステータスバッジ（右上絶対配置） */}
      <span style={{
        position: 'absolute', top: 0, right: 0,
        padding: '4px 14px',
        borderRadius: '0 24px 0 14px',
        background: statusBg, color: statusColor,
        fontSize: 10, fontWeight: 700,
        fontFamily: "'Space Grotesk', sans-serif",
        letterSpacing: '0.5px', textTransform: 'uppercase',
      }}>
        {statusLabel}
      </span>

      {/* 登録記号 + メーカー */}
      <div style={{ paddingRight: 64 }}>
        <div style={{
          fontFamily: "'Space Grotesk', sans-serif",
          fontSize: 20, fontWeight: 700, color: C.primary, letterSpacing: '-0.3px',
          lineHeight: 1.2,
        }}>
          {info?.registration_code || '—'}
        </div>
        <div style={{ fontSize: 12, color: C.onSurfaceVariant, marginTop: 3 }}>
          {info?.manufacturer_jpn || info?.manufacturer_eng || '—'} / {info?.model_jpn || info?.model_eng || '—'}
        </div>
      </div>

      {/* スペック行 */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '6px 12px' }}>
        {[
          { icon: 'category',    label: '機体種類', value: AIRCRAFT_TYPE_LABELS[info?.aircraft_type] || info?.aircraft_type || '—' },
          { icon: 'scale',       label: '機体重量',     value: info?.aircraft_weight ? `${info.aircraft_weight} kg` : '—' },
          { icon: 'event',       label: '登録日',     value: formatDate(info?.effectiveness_period_self) },
          { icon: 'event_busy',  label: '有効期限',     value: formatDate(info?.effectiveness_period_to) },
        ].map(({ icon, label, value }) => (
          <div key={label} style={{ background: C.surfaceContainerLow, borderRadius: 10, padding: '8px 10px' }}>
            <div style={{ fontSize: 9, fontFamily: "'Space Grotesk', sans-serif", fontWeight: 600, color: C.outline, textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: 2 }}>
              {label}
            </div>
            <div style={{ fontSize: 13, fontWeight: 500, color: C.onSurface }}>{value}</div>
          </div>
        ))}
      </div>

      {/* セパレータ（中央から左右に青くなる hover 効果） */}
      <div style={{ position: 'relative', height: 1, marginBottom: 10 }}>
        <div style={{ position: 'absolute', inset: 0, background: 'rgba(193,199,211,0.35)' }} />
        <div style={{
          position: 'absolute', inset: 0,
          background: C.primaryContainer,
          transform: hovered ? 'scaleX(1)' : 'scaleX(0)',
          transformOrigin: 'center',
          transition: 'transform 0.3s ease',
        }} />
      </div>

      {/* アクションフッター */}
      <div style={{
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
      }}>
        <button
          onClick={onFlightPlan}
          style={{
            display: 'inline-flex', alignItems: 'center', gap: 4,
            padding: '4px 10px', borderRadius: 9999,
            border: `1px solid rgba(193,199,211,0.6)`,
            background: 'transparent', color: C.onSurfaceVariant,
            fontSize: 11, fontWeight: 500,
            fontFamily: "'Space Grotesk', sans-serif", cursor: 'pointer',
          }}
        >
          <span className="material-symbols-outlined" style={{ fontSize: 13 }}>calendar_month</span>
          飛行計画
        </button>
        <span style={{
          display: 'inline-flex', alignItems: 'center', gap: 4,
          fontSize: 12, fontWeight: 600, color: C.primaryContainer,
          fontFamily: "'Space Grotesk', sans-serif",
        }}>
          詳細を表示
          <span className="material-symbols-outlined" style={{ fontSize: 14 }}>arrow_forward</span>
        </span>
      </div>
    </div>
  );
}

// ---- タブボタン ----
const DRAWER_TABS = [
  { id: 'basic',    label: '基本情報',       icon: 'info' },
  { id: 'rid',      label: 'リモートID',     icon: 'wifi_tethering' },
  { id: 'spec',     label: 'スペック',       icon: 'straighten' },
  { id: 'raw',      label: '生データ',       icon: 'data_object' },
];

// ---- 機体詳細ボトムシートドロワー ----
function AircraftDrawer({ aircraft, tab, onTabChange, onClose }) {
  const info = getAircraftInfo(aircraft);
  const isActive = info?.aircraft_status === '1';

  return (
    <div
      style={{
        position: 'fixed', inset: 0,
        zIndex: 200,
        display: 'flex', alignItems: 'flex-end', justifyContent: 'center',
        animation: 'overlayIn 0.7s cubic-bezier(0.4,0,0.2,1) both',
      }}
      onClick={onClose}
    >
      <style>{`
        @keyframes slideUp { from { transform: translateY(100%); } to { transform: translateY(0); } }
        @keyframes overlayIn {
          from { background: rgba(15,23,42,0); backdrop-filter: blur(0px); -webkit-backdrop-filter: blur(0px); }
          to   { background: rgba(15,23,42,0.42); backdrop-filter: blur(8px); -webkit-backdrop-filter: blur(8px); }
        }
      `}</style>
      <div
        style={{
          width: '100%', maxWidth: 860,
          background: 'rgba(248,249,255,0.62)',
          backdropFilter: 'blur(28px)',
          WebkitBackdropFilter: 'blur(28px)',
          borderRadius: '40px 40px 0 0',
          border: '1px solid rgba(255,255,255,0.32)',
          boxShadow: '0 -20px 60px rgba(0,0,0,0.12)',
          padding: '0 0 32px',
          maxHeight: '85vh',
          display: 'flex', flexDirection: 'column',
          position: 'relative',
          animation: 'slideUp 0.7s cubic-bezier(0.4,0,0.2,1) both',
        }}
        onClick={e => e.stopPropagation()}
      >
        {/* プルハンドル */}
        <div style={{ display: 'flex', justifyContent: 'center', padding: '16px 0 8px' }}>
          <div style={{ width: 64, height: 5, borderRadius: 9999, background: C.outlineVariant }} />
        </div>

        {/* ドロワーヘッダー */}
        <div style={{ padding: '8px 32px 0', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <div>
            <span style={{ fontSize: 10, fontFamily: "'Space Grotesk', sans-serif", fontWeight: 600, letterSpacing: '1px', color: C.primary, textTransform: 'uppercase' }}>
              機体詳細
            </span>
            <h2 style={{ fontFamily: "'Space Grotesk', sans-serif", fontSize: 28, fontWeight: 700, color: C.primary, margin: '2px 0 0', letterSpacing: '-0.5px' }}>
              {info?.registration_code || '—'}
            </h2>
            <p style={{ fontSize: 13, color: C.onSurfaceVariant, margin: '2px 0 0' }}>
              {info?.manufacturer_jpn || info?.manufacturer_eng || '—'} / {info?.model_jpn || info?.model_eng || '—'}
            </p>
          </div>
          {/* ステータス + 閉じるボタン */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <StatusBadge variant={isActive ? 'secondary' : 'neutral'}>
              {isActive ? '有効' : info?.aircraft_status === '2' ? '期限切れ' : '抹消済'}
            </StatusBadge>
            <button
              onClick={onClose}
              style={{
                width: 40, height: 40, borderRadius: '50%',
                border: `1px solid ${C.outlineVariant}`,
                background: 'rgba(255,255,255,0.6)', cursor: 'pointer',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                color: C.onSurfaceVariant,
              }}
            >
              <span className="material-symbols-outlined" style={{ fontSize: 20 }}>close</span>
            </button>
          </div>
        </div>

        {/* タブバー */}
        <div style={{ display: 'flex', gap: 8, padding: '16px 32px 0', borderBottom: `1px solid ${C.outlineVariant}`, paddingBottom: 0 }}>
          {DRAWER_TABS.map(t => (
            <button
              key={t.id}
              onClick={() => onTabChange(t.id)}
              style={{
                display: 'inline-flex', alignItems: 'center', gap: 6,
                padding: '8px 16px',
                borderRadius: '12px 12px 0 0',
                border: 'none',
                background: tab === t.id ? C.primaryContainer : 'transparent',
                color: tab === t.id ? C.onPrimary : C.onSurfaceVariant,
                fontSize: 13, fontWeight: tab === t.id ? 700 : 500,
                fontFamily: "'Space Grotesk', sans-serif",
                cursor: 'pointer',
                transition: 'background 0.15s, color 0.15s',
                marginBottom: -1,
                borderBottom: tab === t.id ? 'none' : 'none',
              }}
            >
              <span className="material-symbols-outlined" style={{ fontSize: 15, fontVariationSettings: tab === t.id ? "'FILL' 1" : "'FILL' 0" }}>
                {t.icon}
              </span>
              {t.label}
            </button>
          ))}
        </div>

        {/* タブコンテンツ（全タブ固定高さ・スクロール） */}
        <div style={{ height: 300, overflowY: 'auto', padding: '24px 32px', flexShrink: 0 }}>
          {tab === 'basic' && (
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: 12 }}>
              {[
                { label: '登録記号',   value: info?.registration_code },
                { label: '製造番号',   value: info?.manufacturing_number },
                { label: '機体種類',   value: AIRCRAFT_TYPE_LABELS[info?.aircraft_type] || info?.aircraft_type },
                { label: 'メーカー',   value: info?.manufacturer_jpn || info?.manufacturer_eng },
                { label: 'モデル',     value: info?.model_jpn || info?.model_eng },
                { label: '登録日',     value: formatDate(info?.effectiveness_period_self) },
                { label: '有効期限',   value: formatDate(info?.effectiveness_period_to) },
                { label: '最終更新日', value: formatDate(info?.last_update_date) },
              ].map(({ label, value }) => (
                <InfoCell key={label} label={label} value={value} />
              ))}
            </div>
          )}

          {tab === 'rid' && (
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: 12 }}>
              {[
                { label: 'RID製造番号', value: info?.rid_manufacturing_number },
                { label: 'RIDメーカー', value: info?.rid_manufacturer_jpn || info?.rid_manufacturer_eng },
                { label: 'RID型式',     value: info?.rid_model_jpn || info?.rid_model_eng },
                { label: 'RID種類',     value: info?.rid_type === '1' ? '内蔵型' : info?.rid_type === '2' ? '外付け型' : info?.rid_type },
              ].map(({ label, value }) => (
                <InfoCell key={label} label={label} value={value} />
              ))}
            </div>
          )}

          {tab === 'spec' && (
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: 12 }}>
              {[
                { label: '機体重量',       value: info?.aircraft_weight ? `${info.aircraft_weight} kg` : null },
                { label: '最大離陸重量',   value: info?.maximum_takeoff_weight ? `${info.maximum_takeoff_weight} kg` : null },
                { label: '全長',           value: info?.aircraft_length ? `${info.aircraft_length} m` : null },
                { label: '全幅',           value: info?.aircraft_width ? `${info.aircraft_width} m` : null },
                { label: '全高',           value: info?.aircraft_height ? `${info.aircraft_height} m` : null },
              ].map(({ label, value }) => (
                <InfoCell key={label} label={label} value={value} />
              ))}
            </div>
          )}

          {tab === 'raw' && (
            <div style={{
              background: C.surfaceContainerLow,
              borderRadius: 16, padding: 20,
              border: `1px solid ${C.outlineVariant}`,
            }}>
              <pre style={{ fontSize: 11, color: C.onSurfaceVariant, whiteSpace: 'pre-wrap', wordBreak: 'break-all', margin: 0, fontFamily: 'monospace' }}>
                {JSON.stringify(aircraft, null, 2)}
              </pre>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ---- ヘッダーアクションボタン ----
function HeaderActionBtn({ icon, label, onClick, disabled }) {
  const [hovered, setHovered] = useState(false);
  return (
    <button
      onClick={onClick}
      disabled={disabled}
      style={{
        display: 'inline-flex', alignItems: 'center', gap: 6,
        padding: '7px 16px', borderRadius: 9999,
        border: `1px solid ${C.outlineVariant}`,
        background: hovered ? C.surfaceContainerLow : 'rgba(255,255,255,0.80)',
        color: C.onSurfaceVariant,
        fontSize: 13, fontWeight: 500,
        fontFamily: "'Space Grotesk', sans-serif",
        cursor: disabled ? 'not-allowed' : 'pointer',
        opacity: disabled ? 0.5 : 1,
        transition: 'background 0.15s, color 0.15s',
        backdropFilter: 'blur(12px)',
      }}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
    >
      <span className="material-symbols-outlined" style={{ fontSize: 16 }}>{icon}</span>
      {label}
    </button>
  );
}

// ---- DIPS2.0 認証ボタン（ヘッダー用） ----
function AuthButton({ onClick, disabled, loading }) {
  const [hovered, setHovered] = useState(false);
  return (
    <button
      onClick={onClick}
      disabled={disabled}
      style={{
        display: 'inline-flex', alignItems: 'center', gap: 8,
        padding: '9px 20px', borderRadius: 9999,
        border: 'none',
        background: hovered ? '#00508e' : C.primaryContainer,
        color: C.onPrimary,
        fontSize: 14, fontWeight: 600,
        fontFamily: "'Space Grotesk', sans-serif",
        cursor: disabled ? 'not-allowed' : 'pointer',
        opacity: disabled ? 0.6 : 1,
        boxShadow: hovered ? '0 4px 16px rgba(0,104,183,0.30)' : '0 2px 8px rgba(0,104,183,0.18)',
        transform: hovered ? 'scale(1.03)' : 'scale(1)',
        transition: 'background 0.15s, box-shadow 0.15s, transform 0.15s',
      }}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
    >
      <span className="material-symbols-outlined" style={{ fontSize: 18, display: 'inline-block', transform: 'rotate(-45deg)' }}>link</span>
      {loading ? '接続中...' : 'DIPS2.0 認証'}
    </button>
  );
}

// ---- 未連携状態カード ----
function UnauthenticatedCard({ onAuth, loading }) {
  const [btnHovered, setBtnHovered] = useState(false);
  return (
    <div style={{
      background: 'rgba(255,255,255,0.72)',
      backdropFilter: 'blur(20px)',
      WebkitBackdropFilter: 'blur(20px)',
      borderRadius: 24,
      border: '1px solid rgba(255,255,255,0.28)',
      boxShadow: '0 8px 32px rgba(0,104,183,0.10)',
      padding: '64px 24px',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      gap: 0,
      textAlign: 'center',
    }}>
      {/* チェーンアイコン */}
      <div style={{
        width: 88, height: 88,
        borderRadius: '50%',
        background: `linear-gradient(135deg, ${C.primaryContainer} 0%, #0050a0 100%)`,
        boxShadow: '0 8px 28px rgba(0,104,183,0.28)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        marginBottom: 28,
      }}>
        <span
          className="material-symbols-outlined"
          style={{
            fontSize: 44, color: C.onPrimary,
            fontVariationSettings: "'FILL' 1, 'wght' 400, 'GRAD' 0, 'opsz' 48",
            transform: 'rotate(-45deg)',
            display: 'block',
          }}
        >
          link
        </span>
      </div>

      {/* 見出し */}
      <h2 style={{
        fontFamily: "'Space Grotesk', sans-serif",
        fontSize: 22, fontWeight: 700, letterSpacing: '-0.3px',
        color: C.onSurface, margin: 0, marginBottom: 12,
      }}>
        DIPS2.0 と連携する
      </h2>

      {/* 説明文 */}
      <p style={{
        fontSize: 15, color: C.onSurfaceVariant, lineHeight: 1.7,
        maxWidth: 420, margin: '0 auto 32px',
      }}>
        <span style={{ whiteSpace: 'nowrap' }}>国土交通省のドローン情報基盤システム（DIPS2.0）と連携すると、</span><br />
        登録されている機体情報を確認できます。
      </p>

      {/* 認証開始ボタン */}
      <button
        onClick={onAuth}
        disabled={loading}
        style={{
          display: 'inline-flex', alignItems: 'center', gap: 10,
          padding: '13px 32px', borderRadius: 9999,
          border: 'none',
          background: btnHovered ? '#00508e' : C.primaryContainer,
          color: C.onPrimary,
          fontSize: 15, fontWeight: 700,
          fontFamily: "'Space Grotesk', sans-serif",
          cursor: loading ? 'not-allowed' : 'pointer',
          opacity: loading ? 0.6 : 1,
          boxShadow: btnHovered
            ? '0 6px 24px rgba(0,104,183,0.38)'
            : '0 4px 16px rgba(0,104,183,0.22)',
          transform: btnHovered ? 'translateY(-2px) scale(1.03)' : 'translateY(0) scale(1)',
          transition: 'background 0.15s, box-shadow 0.15s, transform 0.18s',
        }}
        onMouseEnter={() => setBtnHovered(true)}
        onMouseLeave={() => setBtnHovered(false)}
      >
        <span
          className="material-symbols-outlined"
          style={{ fontSize: 20, fontVariationSettings: "'FILL' 1", display: 'inline-block', transform: 'rotate(-45deg)' }}
        >
          link
        </span>
        DIPS2.0 認証を開始
      </button>

      {/* 補足テキスト */}
      <p style={{ fontSize: 12, color: C.outline, marginTop: 20, fontFamily: "'Space Grotesk', sans-serif" }}>
        DIPS のログインページが別ウィンドウで開きます
      </p>
    </div>
  );
}
